import React, { useCallback, useEffect, useRef, useState } from 'react'
import apiService from '../api/apiService'
import { useAuth } from '../contexts/AuthContext'
import RecipeSelectionModal from './MealCalendar/RecipeSelectionModal'
import { Button } from './ui'

/**
 * MealCalendar component for displaying a 7-day meal planning calendar
 * with drag and drop functionality for recipe cards.
 * A user can only have one calendar, which is either individual or shared.
 * When shared, it shows a combined view of both users' meals with no distinction between users.
 */
const MealCalendar = ({
  isVisible,
  onAddToIngredients,
  style,
  selectedRecipe,
}) => {
  const [days, setDays] = useState([])
  const [mealPlan, setMealPlan] = useState({})
  const [currentUserId, setCurrentUserId] = useState(null)
  const [dragOverInfo, setDragOverInfo] = useState(null)
  const [loading, setLoading] = useState(true)
  const [mealLoading, setMealLoading] = useState({
    dayIndex: null,
    mealNumber: null,
  })
  const [showRecipeModal, setShowRecipeModal] = useState(false)
  const [selectedDayInfo, setSelectedDayInfo] = useState(null)
  const { user } = useAuth()

  // Helper function to truncate text based on screen size
  const truncateText = (text, isMobile = window.innerWidth < 1024) => {
    const maxLength = isMobile ? 35 : 65 // Increased from 28:45 to 45:65 for longer meal names
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
  }

  // Maintain a cache for calendar data
  const calendarDataCache = useRef({
    data: null,
    timestamp: null,
    expiresInMinutes: 5, // Cache expires after 5 minutes
  })

  // Generate days of the week if needed as fallback
  const generateDaysOfWeek = useCallback(() => {
    const today = new Date()
    const daysOfWeek = []

    // Create array of 7 days starting from today
    for (let i = 0; i < 7; i++) {
      const date = new Date(today)
      date.setDate(today.getDate() + i)

      daysOfWeek.push({
        date,
        name: date.toLocaleDateString('en-US', { weekday: 'long' }),
        shortName: date.toLocaleDateString('en-US', { weekday: 'short' }),
        formattedDate: date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        }),
        isoDate: date.toISOString().split('T')[0], // YYYY-MM-DD format for database
      })
    }

    return daysOfWeek
  }, [])

  // Initialize the days of the week and fetch meal plans in a single effect
  useEffect(() => {
    if (!isVisible || !user) return

    // Load calendar data with cache support
    const loadCalendarData = async () => {
      setLoading(true)

      try {
        // Check if we have valid cached data
        const now = new Date()
        const cacheIsValid =
          calendarDataCache.current.data &&
          calendarDataCache.current.timestamp &&
          (now - calendarDataCache.current.timestamp) / (1000 * 60) <
            calendarDataCache.current.expiresInMinutes

        // Use cached data if available and valid
        if (cacheIsValid) {
          const { userId, days, mealPlansByDay } =
            calendarDataCache.current.data

          // Format days to include isoDate for backward compatibility
          const formattedDays = days.map((day) => ({
            ...day,
            name: new Date(day.date).toLocaleDateString('en-US', {
              weekday: 'long',
            }),
            formattedDate: day.displayDate,
            isoDate: day.date, // Add isoDate prop for backward compatibility
          }))

          setDays(formattedDays)
          setCurrentUserId(userId)
          setMealPlan(mealPlansByDay)
          setLoading(false)
          return
        }

        // Fetch all calendar data in a single request
        const response = await fetch(`/api/meal-calendar/${user.email}`)

        if (!response.ok) {
          console.error(
            'Error response from meal calendar API:',
            response.status,
            response.statusText
          )
          throw new Error('Failed to fetch meal calendar data')
        }

        const calendarData = await response.json()

        // Format days to include isoDate for backward compatibility
        const formattedDays = calendarData.days.map((day) => ({
          ...day,
          name: new Date(day.date).toLocaleDateString('en-US', {
            weekday: 'long',
          }),
          formattedDate: day.displayDate,
          isoDate: day.date, // Add isoDate prop for backward compatibility
        }))

        // Update state with the response data
        setDays(formattedDays)
        setCurrentUserId(calendarData.userId)
        setMealPlan(calendarData.mealPlansByDay)

        // Store in cache
        calendarDataCache.current = {
          data: {
            ...calendarData,
            days: formattedDays,
          },
          timestamp: new Date(),
          expiresInMinutes: 5,
        }
      } catch (error) {
        console.error('Error loading calendar data:', error)

        // Fallback to generating days if the API call fails
        const daysOfWeek = generateDaysOfWeek()
        setDays(daysOfWeek)
      } finally {
        setLoading(false)
      }
    }

    loadCalendarData()
  }, [isVisible, user, generateDaysOfWeek])

  // Force reload calendar data when needed (after adding/removing meals)
  const reloadCalendarData = useCallback(async () => {
    // Invalidate cache
    calendarDataCache.current.data = null

    if (!isVisible || !user) return

    setLoading(true)

    try {
      // Fetch all calendar data in a single request
      const response = await fetch(`/api/meal-calendar/${user.email}`)

      if (!response.ok) {
        throw new Error('Failed to fetch meal calendar data')
      }

      const calendarData = await response.json()

      // Format days to include isoDate for backward compatibility
      const formattedDays = calendarData.days.map((day) => ({
        ...day,
        name: new Date(day.date).toLocaleDateString('en-US', {
          weekday: 'long',
        }),
        formattedDate: day.displayDate,
        isoDate: day.date, // Add isoDate prop for backward compatibility
      }))

      // Update state with the response data
      setDays(formattedDays)
      setCurrentUserId(calendarData.userId)
      setMealPlan(calendarData.mealPlansByDay)

      // Store in cache
      calendarDataCache.current = {
        data: {
          ...calendarData,
          days: formattedDays,
        },
        timestamp: new Date(),
        expiresInMinutes: 5,
      }
    } catch (error) {
      console.error('Error reloading calendar data:', error)
    } finally {
      setLoading(false)
    }
  }, [isVisible, user])

  // Helper function to get meal from the meal plan
  const getMeal = (dayIndex, mealNumber) => {
    if (!days || days.length === 0) return null

    const dateKey = days[dayIndex]?.isoDate || days[dayIndex]?.date
    if (!dateKey) return null

    return mealPlan[dateKey]?.[mealNumber] || null
  }

  // Handle adding a meal to the plan
  const handleAddMeal = async (dayIndex, mealNumber) => {
    if (!user || !currentUserId) return

    const day = days[dayIndex]

    if (selectedRecipe) {
      // If we have a selected recipe, add it directly
      try {
        // Update the UI immediately (optimistically)
        setMealPlan((prevMealPlan) => {
          const newMealPlan = { ...prevMealPlan }
          if (!newMealPlan[day.isoDate]) {
            newMealPlan[day.isoDate] = {}
          }
          newMealPlan[day.isoDate][mealNumber] = {
            name: selectedRecipe.name,
            id: selectedRecipe.id,
            dbId: 'pending', // Temporary ID until we get the response
            userId: currentUserId,
          }
          return newMealPlan
        })

        // Add the meal to the database
        const mealPlanData = {
          user_id: currentUserId,
          date: day.isoDate,
          meal_number: mealNumber,
          recipe_id: selectedRecipe.id,
        }

        // Add using the API
        const response = await apiService.addMealPlan(mealPlanData)

        // Update the state with the real database ID
        setMealPlan((prevMealPlan) => {
          const newMealPlan = { ...prevMealPlan }
          if (newMealPlan[day.isoDate]?.[mealNumber]) {
            newMealPlan[day.isoDate][mealNumber].dbId = response.id
          }
          return newMealPlan
        })
      } catch (error) {
        console.error('Error adding recipe to meal plan:', error)
        // Revert the optimistic update on error
        setMealPlan((prevMealPlan) => {
          const newMealPlan = { ...prevMealPlan }
          if (newMealPlan[day.isoDate]) {
            delete newMealPlan[day.isoDate][mealNumber]
            if (Object.keys(newMealPlan[day.isoDate]).length === 0) {
              delete newMealPlan[day.isoDate]
            }
          }
          return newMealPlan
        })
      }
    } else {
      // If no selected recipe, show recipe selection modal for both mobile and desktop
      setSelectedDayInfo({ dayIndex, mealNumber, day })
      setShowRecipeModal(true)
    }
  }

  // Handle recipe selection from modal
  const handleRecipeSelect = async (recipe) => {
    if (!selectedDayInfo || !user || !currentUserId) return

    const { dayIndex, mealNumber, day } = selectedDayInfo

    // Hide the modal immediately after selection for better UX
    setShowRecipeModal(false)

    // Clear selected day info immediately
    const dayInfoCopy = { ...selectedDayInfo }
    setSelectedDayInfo(null)

    try {
      // Update the UI immediately (optimistically)
      setMealPlan((prevMealPlan) => {
        const newMealPlan = { ...prevMealPlan }
        if (!newMealPlan[day.isoDate]) {
          newMealPlan[day.isoDate] = {}
        }
        newMealPlan[day.isoDate][mealNumber] = {
          name: recipe.name,
          id: recipe.id,
          recipe_id: recipe.id,
          dbId: 'pending',
          userId: currentUserId,
        }
        return newMealPlan
      })

      // Prepare meal plan data
      const mealPlanData = {
        user_id: currentUserId,
        date: day.isoDate,
        meal_number: mealNumber,
        recipe_id: recipe.id,
      }

      // Insert the meal plan using API service
      const response = await apiService.addMealPlan(mealPlanData)

      // Update the state with the real database ID
      setMealPlan((prevMealPlan) => {
        const newMealPlan = { ...prevMealPlan }
        if (newMealPlan[day.isoDate]?.[mealNumber]) {
          newMealPlan[day.isoDate][mealNumber].dbId = response.id
          newMealPlan[day.isoDate][mealNumber].recipe_id = recipe.id
        }
        return newMealPlan
      })
    } catch (error) {
      console.error('Error adding recipe to meal plan:', error)
      // Revert the optimistic update on error
      setMealPlan((prevMealPlan) => {
        const newMealPlan = { ...prevMealPlan }
        if (newMealPlan[day.isoDate]) {
          delete newMealPlan[day.isoDate][mealNumber]
          if (Object.keys(newMealPlan[day.isoDate]).length === 0) {
            delete newMealPlan[day.isoDate]
          }
        }
        return newMealPlan
      })
      // Show the modal again on error
      setSelectedDayInfo(dayInfoCopy)
      setShowRecipeModal(true)
    }
  }

  // Handle removing a meal from the plan
  const handleRemoveMeal = async (dayIndex, mealNumber) => {
    if (!user || !currentUserId) return

    const meal = getMeal(dayIndex, mealNumber)
    const dateKey = days[dayIndex].isoDate

    if (!meal || !meal.dbId) return

    try {
      // Store the meal data for potential rollback
      const originalMeal = { ...meal }

      // Update the UI immediately (optimistically)
      setMealPlan((prevMealPlan) => {
        const newMealPlan = { ...prevMealPlan }
        if (newMealPlan[dateKey]) {
          delete newMealPlan[dateKey][mealNumber]
          if (Object.keys(newMealPlan[dateKey]).length === 0) {
            delete newMealPlan[dateKey]
          }
        }
        return newMealPlan
      })

      // Delete the meal plan using API service
      await apiService.removeMealPlan(meal.dbId, currentUserId)
    } catch (error) {
      console.error('Error removing meal plan:', error)
      // Revert the optimistic update on error
      setMealPlan((prevMealPlan) => {
        const newMealPlan = { ...prevMealPlan }
        if (!newMealPlan[dateKey]) {
          newMealPlan[dateKey] = {}
        }
        newMealPlan[dateKey][mealNumber] = meal
        return newMealPlan
      })
    }
  }

  // Handle drag over event
  const handleDragOver = (e, dayIndex, mealNumber) => {
    e.preventDefault()
    setDragOverInfo({ dayIndex, mealNumber })
  }

  // Handle drag leave event
  const handleDragLeave = () => {
    setDragOverInfo(null)
  }

  // Handle drop event
  const handleDrop = async (e, dayIndex, mealNumber) => {
    e.preventDefault()
    setDragOverInfo(null)

    if (!user || !currentUserId) return

    try {
      const recipeData = JSON.parse(e.dataTransfer.getData('recipe'))
      if (recipeData && recipeData.id) {
        const dateKey = days[dayIndex].isoDate

        // Check if there's already a meal plan for this day and meal number
        const existingMeal = mealPlan[dateKey]?.[mealNumber]

        // Update the UI immediately (optimistically)
        setMealPlan((prevMealPlan) => {
          const newMealPlan = { ...prevMealPlan }
          if (!newMealPlan[dateKey]) {
            newMealPlan[dateKey] = {}
          }
          newMealPlan[dateKey][mealNumber] = {
            name: recipeData.name,
            id: recipeData.id,
            dbId: 'pending',
            userId: currentUserId,
          }
          return newMealPlan
        })

        // Remove the existing meal plan if it exists
        if (existingMeal && existingMeal.dbId) {
          await apiService.removeMealPlan(existingMeal.dbId, currentUserId)
        }

        // Add a new meal plan
        const mealPlanData = {
          user_id: currentUserId,
          date: dateKey,
          meal_number: mealNumber,
          recipe_id: recipeData.id,
        }

        const response = await apiService.addMealPlan(mealPlanData)

        // Update the state with the real database ID
        setMealPlan((prevMealPlan) => {
          const newMealPlan = { ...prevMealPlan }
          if (newMealPlan[dateKey]?.[mealNumber]) {
            newMealPlan[dateKey][mealNumber].dbId = response.id
          }
          return newMealPlan
        })
      }
    } catch (error) {
      console.error('Error processing dropped recipe:', error)
      // Revert the optimistic update on error
      setMealPlan((prevMealPlan) => {
        const newMealPlan = { ...prevMealPlan }
        const dateKey = days[dayIndex].isoDate
        if (newMealPlan[dateKey]) {
          delete newMealPlan[dateKey][mealNumber]
          if (Object.keys(newMealPlan[dateKey]).length === 0) {
            delete newMealPlan[dateKey]
          }
        }
        return newMealPlan
      })
    }
  }

  // Function to collect all recipe IDs from the meal plan
  const handleAddToIngredients = () => {
    if (!onAddToIngredients) return

    const recipeIds = []
    let loadingState = false;

    // Iterate through all days and meal numbers to collect recipe IDs
    Object.values(mealPlan).forEach((dayMeals) => {
      Object.values(dayMeals).forEach((meal) => {
        if (meal.recipe_id || meal.id) {
          recipeIds.push(meal.recipe_id || meal.id)
          loadingState = true;
        }
      })
    })

    // Call the parent component's function with all recipe IDs
    if (recipeIds.length > 0) {
      // Show visual feedback on the button
      const ingredientsBtn = document.querySelector('.calendar-ingredients-btn');
      if (ingredientsBtn) {
        ingredientsBtn.classList.add('bg-green-100');
        setTimeout(() => {
          ingredientsBtn.classList.remove('bg-green-100');
        }, 500);
      }
      
      onAddToIngredients(recipeIds)
    }
  }

  if (!isVisible || !user) return null

  if (loading) {
    return (
      <div
        className="w-full bg-white rounded-lg border border-gray-200 p-4 flex flex-col items-center justify-center"
        style={{ minHeight: '300px' }}
      >
        <div className="w-10 h-10 border-4 border-green-500 border-t-transparent rounded-full animate-spin mb-4"></div>
        <p className="text-center text-gray-600">Loading meal calendar...</p>
      </div>
    )
  }

  return (
    <>
      <div className="bg-white rounded-lg border border-gray-200 flex flex-col">
        <div className="p-2 lg:p-4 border-b border-gray-200 flex justify-between items-center bg-white z-10 rounded-t-lg w-full">
          <div className="flex items-center">
            <h2 className="text-xl font-semibold text-gray-800">Calendar</h2>
          </div>

          {onAddToIngredients && (
            <Button
              variant="text"
              size="sm"
              onClick={handleAddToIngredients}
              className="text-green-600 hover:bg-green-50 calendar-ingredients-btn"
              disabled={loading || Object.keys(mealPlan).length === 0}
            >
              Ingredients
            </Button>
          )}
        </div>

        <div className="p-4 flex-1 w-full">
          <div className="flex flex-col gap-1 lg:gap-3 w-full">
            {days.map((day, dayIndex) => (
              <div key={dayIndex} className="flex flex-col w-full mb-1">
                <div className="flex justify-between items-center mb-1 w-full">
                  <h3 className="font-sm text-gray-800">{day.name}</h3>
                  <span className="text-sm text-gray-500">
                    {day.formattedDate}
                  </span>
                </div>
                <div className="flex flex-col gap-1 w-full">
                  {[1, 2].map((mealNumber, mealIndex) => {
                    const meal = getMeal(dayIndex, mealNumber)
                    const isDragOver =
                      dragOverInfo &&
                      dragOverInfo.dayIndex === dayIndex &&
                      dragOverInfo.mealNumber === mealNumber
                    const isLoading =
                      mealLoading.dayIndex === dayIndex &&
                      mealLoading.mealNumber === mealNumber

                    return (
                      <div
                        key={mealNumber}
                        className="flex items-center gap-2"
                        onDragOver={(e) =>
                          handleDragOver(e, dayIndex, mealNumber)
                        }
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, dayIndex, mealNumber)}
                        data-meal-number={mealNumber}
                      >
                        <div
                          className={`w-full rounded-md border ${
                            isDragOver
                              ? 'border-green-300 bg-green-50'
                              : meal
                                ? 'border-gray-200 bg-blue-50'
                                : 'border-gray-200 border-dashed'
                          } p-1 transition-colors lg:w-full lg:h-auto h-[24px] lg:h-[28px]`}
                          data-meal-number={mealNumber}
                        >
                          {isLoading ? (
                            <div className="flex justify-center items-center h-full">
                              <div className="w-5 h-5 border-2 border-green-500 border-t-transparent rounded-full animate-spin"></div>
                            </div>
                          ) : meal ? (
                            <div className="flex justify-between items-center h-full">
                              <div className="flex-1 text-xs font-medium text-gray-800 text-center pr-2">
                                {truncateText(meal.name)}
                              </div>
                              <button
                                onClick={() =>
                                  handleRemoveMeal(dayIndex, mealNumber)
                                }
                                className="flex-shrink-0 ml-1 text-red-500 hover:text-red-700 p-1"
                                disabled={isLoading}
                                aria-label="Remove meal"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-4 w-4 lg:h-3 lg:w-3"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                  />
                                </svg>
                              </button>
                            </div>
                          ) : (
                            <div className="border-dashed h-full flex items-center justify-center">
                              <button
                                onClick={() =>
                                  handleAddMeal(dayIndex, mealNumber)
                                }
                                className="text-xs text-gray-500 hover:text-green-600 flex items-center w-full justify-center"
                                disabled={isLoading}
                                data-meal-number={mealNumber}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-3 w-3 mr-1"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 4v16m8-8H4"
                                  />
                                </svg>
                                Add meal
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <RecipeSelectionModal
        isOpen={showRecipeModal}
        onClose={() => {
          setShowRecipeModal(false)
          setSelectedDayInfo(null)
        }}
        onSelectRecipe={handleRecipeSelect}
        dayInfo={selectedDayInfo}
      />
    </>
  )
}

export default MealCalendar
